import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  acceptVendorGuidancePreview,
  cancelVendorGuidancePreview,
  createVendorInvoiceVendor,
  fetchVendorGuidancePreview,
  fetchVendorList,
  startVendorGuidanceBlind,
  startVendorGuidancePreview,
  streamVendorInvoiceGuidanceChat,
  updateVendorInvoiceVendor,
} from '../../utils/agentsApi';
import { fetchAdjustmentPayableReasonCodes } from '../../utils/apiUtils';
import Modal from '../modal/Modal';
import ExpectedPayablesCheck from './vendorInvoice/ExpectedPayablesCheck';
import GuidanceSectionsHint from './vendorInvoice/GuidanceSectionsHint';
import VendorInvoicePayablesTable from './vendorInvoice/VendorInvoicePayablesTable';
import VendorInvoiceReviewSplit from './vendorInvoice/VendorInvoiceReviewSplit';
import VendorInvoiceUploadPanel from './vendorInvoice/VendorInvoiceUploadPanel';
import {
  applyReviewChatEvent,
  GUIDANCE_TEMPLATE,
  JOB_STATUS,
  REQUIRED_GUIDANCE_SECTIONS,
  VENDOR_INVOICE_POLLING,
  VENDOR_INVOICE_QUERY_KEYS,
  VENDOR_INVOICE_ROUTES,
  VENDOR_TYPE,
} from './vendorInvoice/utils';
import { useVendorInvoiceUploads } from './vendorInvoice/useVendorInvoiceUploads';

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  paddingBottom: '2rem',
};

const sectionTitleStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--black)',
};

const emptyStyle = {
  color: 'var(--darkGrey)',
  fontSize: '0.9rem',
};

const noteStyle = {
  fontSize: '0.875rem',
  color: 'var(--darkGrey)',
  marginBottom: '0.75rem',
};

const btnBase = {
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.35rem',
  padding: '0.45rem 0.9rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  background: 'var(--white)',
};

const btnPrimary = {
  ...btnBase,
  background: 'var(--lightBlue)',
  borderColor: 'var(--lightBlue)',
  color: 'var(--white)',
};

const btnSecondary = { ...btnBase };

const disabledBtn = (style, disabled) => ({
  ...style,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const fieldControlStyle = {
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.35rem',
  padding: '0.5rem 0.75rem',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  color: 'var(--black)',
};

const emptyForm = () => ({
  name: '',
  reasonCode: '',
  supplierHccID: '',
  guidance: GUIDANCE_TEMPLATE,
});

const VendorConfigurationEditor = () => {
  const { vendorName: vendorNameParam } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isCreate = !vendorNameParam || vendorNameParam === 'new';
  const editName = isCreate ? null : decodeURIComponent(vendorNameParam);

  const [form, setForm] = useState(emptyForm);
  // Not part of the vendor config: Test guidance checks its payables against it.
  const [expectedPayables, setExpectedPayables] = useState('');
  const [previewJobId, setPreviewJobId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testing, setTesting] = useState(false);
  const [blindMode, setBlindMode] = useState(false);
  const [latestSuggestedGuidance, setLatestSuggestedGuidance] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [liveChatMessages, setLiveChatMessages] = useState(null);
  const [blindAutoStartJobId, setBlindAutoStartJobId] = useState(null);
  const chatAbortRef = useRef(null);
  const [hydrated, setHydrated] = useState(isCreate);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const { pendingFiles, addFiles, removeFile, readyFiles, uploading } =
    useVendorInvoiceUploads();

  const { data: vendorListData, isLoading: vendorsLoading } = useQuery({
    queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
    queryFn: fetchVendorList,
    enabled: !isCreate,
  });

  const { data: reasonCodesData, isLoading: reasonCodesLoading } = useQuery({
    queryKey: ['adjustmentPayableReasonCodes'],
    queryFn: () => fetchAdjustmentPayableReasonCodes(),
    staleTime: 5 * 60 * 1000,
  });

  const reasonCodeOptions = useMemo(() => {
    if (!reasonCodesData?.rows) {
      return [];
    }
    return reasonCodesData.rows
      .filter((rc) => rc.adjustmentPayableReasonKey >= 0)
      .sort((a, b) =>
        (a.adjustmentPayableReasonName || '').localeCompare(
          b.adjustmentPayableReasonName || '',
        ),
      )
      .map((rc) => ({
        value: rc.adjustmentPayableReasonCode,
        label: `${rc.adjustmentPayableReasonCode} - ${rc.adjustmentPayableReasonName}`,
      }));
  }, [reasonCodesData]);

  useEffect(() => {
    if (isCreate) {
      setForm(emptyForm());
      setHydrated(true);
      return;
    }
    const vendors = vendorListData?.vendors ?? [];
    const match = vendors.find((v) => v.name === editName);
    if (match) {
      setForm({
        name: match.name,
        reasonCode: match.reasonCode ?? '',
        supplierHccID: match.supplierHccID ?? '',
        guidance: match.guidance ?? '',
      });
      setHydrated(true);
      return;
    }
    if (!vendorsLoading && vendorListData) {
      toast.error(`Vendor "${editName}" not found.`);
      navigate(VENDOR_INVOICE_ROUTES.VENDORS, { replace: true });
    }
  }, [isCreate, editName, vendorListData, vendorsLoading, navigate]);

  const goToList = useCallback(
    async ({ cancelPreview = true } = {}) => {
      if (cancelPreview && previewJobId) {
        try {
          await cancelVendorGuidancePreview(previewJobId);
        } catch {
          // best-effort cleanup
        }
      }
      navigate(VENDOR_INVOICE_ROUTES.VENDORS);
    },
    [navigate, previewJobId],
  );

  const canTest =
    Boolean(form.name.trim()) &&
    Boolean(form.guidance.trim()) &&
    readyFiles.length > 0 &&
    !uploading &&
    !testing &&
    (!previewJobId || preview?.status === JOB_STATUS.FAILED);

  const saveInvoiceMutation = useMutation({
    mutationFn: async () => {
      setValidationAttempted(true);
      const payload = {
        name: form.name.trim(),
        type: VENDOR_TYPE.INVOICE,
        reasonCode: form.reasonCode.trim(),
        supplierHccID: form.supplierHccID.trim(),
        guidance: form.guidance.trim(),
      };
      if (!payload.name) {
        throw new Error('Name is required.');
      }
      if (!payload.guidance) {
        throw new Error('Guidance is required.');
      }

      // Check for name collision when editing and name has changed
      if (!isCreate && editName && payload.name !== editName) {
        const vendors = vendorListData?.vendors ?? [];
        const collision = vendors.find((v) => v.name === payload.name);
        if (collision) {
          throw new Error(`A vendor named "${payload.name}" already exists.`);
        }
      }

      // Non-blocking nudge if recommended guidance sections are missing.
      const missing = REQUIRED_GUIDANCE_SECTIONS.filter(
        (section) => !payload.guidance.includes(section.heading),
      ).map((section) => section.heading);
      if (missing.length) {
        toast.warn(
          `Saving without recommended guidance sections: ${missing.join(', ')}.`,
        );
      }

      if (isCreate) {
        return createVendorInvoiceVendor(payload);
      }
      return updateVendorInvoiceVendor(editName, payload);
    },
    onSuccess: () => {
      toast.success(isCreate ? 'Vendor created.' : 'Vendor updated.');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
      });
      goToList({ cancelPreview: false });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save vendor.');
    },
  });

  const canSaveInvoice =
    Boolean(form.name.trim()) &&
    Boolean(form.guidance.trim()) &&
    !testing &&
    !previewJobId &&
    !saveInvoiceMutation.isPending;

  const handleTest = async () => {
    setValidationAttempted(true);
    const name = form.name.trim();
    const guidance = form.guidance.trim();
    if (!name || !guidance) {
      toast.error('Name and guidance are required.');
      return;
    }

    // Check for name collision when editing and name has changed
    if (!isCreate && editName && name !== editName) {
      const vendors = vendorListData?.vendors ?? [];
      const collision = vendors.find((v) => v.name === name);
      if (collision) {
        toast.error(`A vendor named "${name}" already exists.`);
        return;
      }
    }

    const s3Keys = readyFiles.map((f) => f.key).filter(Boolean);
    if (!s3Keys.length) {
      return;
    }

    setTesting(true);
    setBlindMode(false);
    setLatestSuggestedGuidance(null);
    setPreview(null);
    setLiveChatMessages(null);
    setChatLoading(false);
    try {
      if (previewJobId) {
        await cancelVendorGuidancePreview(previewJobId, {
          keepAttachments: true,
        }).catch(() => {});
      }
      const result = await startVendorGuidancePreview({
        name,
        originalName: !isCreate && editName ? editName : undefined,
        reasonCode: form.reasonCode.trim(),
        supplierHccID: form.supplierHccID.trim(),
        guidance,
        s3Keys,
        mode: isCreate ? 'create' : 'edit',
        expectedPayables,
      });
      setPreviewJobId(result.jobId);
      toast.info('Test started — processing invoice with your guidance.');
    } catch (err) {
      toast.error(err.message || 'Failed to start guidance test.');
      setTesting(false);
    }
  };

  // Blind start: no guidance yet — chat with the coach against just the
  // uploaded invoice sample to draft a starting guidance to iterate on.
  const canBlindStart =
    Boolean(form.name.trim()) &&
    !form.guidance.trim() &&
    readyFiles.length > 0 &&
    !uploading &&
    !testing &&
    !previewJobId;

  const handleBlindStart = async () => {
    setValidationAttempted(true);
    const name = form.name.trim();
    if (!name) {
      toast.error('Name is required.');
      return;
    }
    const s3Keys = readyFiles.map((f) => f.key).filter(Boolean);
    if (!s3Keys.length) {
      toast.error('Upload a sample invoice first.');
      return;
    }

    if (!isCreate && editName && name !== editName) {
      const vendors = vendorListData?.vendors ?? [];
      if (vendors.find((v) => v.name === name)) {
        toast.error(`A vendor named "${name}" already exists.`);
        return;
      }
    }

    setLatestSuggestedGuidance(null);
    setLiveChatMessages(null);
    setChatLoading(false);
    try {
      const result = await startVendorGuidanceBlind({
        name,
        originalName: !isCreate && editName ? editName : undefined,
        reasonCode: form.reasonCode.trim(),
        supplierHccID: form.supplierHccID.trim(),
        s3Keys,
        mode: isCreate ? 'create' : 'edit',
      });
      setBlindMode(true);
      setPreview({
        status: JOB_STATUS.PENDING_REVIEW,
        payables: [],
        messages: [],
        isBlindGuidance: true,
      });
      setPreviewJobId(result.jobId);
      // Kick the opening coach turn against the uploaded invoice - no
      // operator message needed. Runs via effect once the session is open.
      setBlindAutoStartJobId(result.jobId);
      toast.info('Reading your invoice to draft a starting guidance…');
    } catch (err) {
      toast.error(err.message || 'Failed to start guidance session.');
    }
  };

  useEffect(() => {
    if (!previewJobId) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;
    let toastedFailure = false;

    const stopPolling = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const poll = async () => {
      try {
        const data = await fetchVendorGuidancePreview(previewJobId);
        if (cancelled) {
          return;
        }
        setPreview(data);
        if (data.status === JOB_STATUS.PENDING_REVIEW) {
          setTesting(false);
          stopPolling();
        } else if (data.status === JOB_STATUS.FAILED) {
          setTesting(false);
          if (!toastedFailure) {
            toastedFailure = true;
            toast.error(data.failureReason || 'Guidance test failed.');
          }
          stopPolling();
        }
      } catch (err) {
        if (!cancelled) {
          setTesting(false);
          if (!toastedFailure) {
            toastedFailure = true;
            toast.error(err.message || 'Failed to load preview results.');
          }
          stopPolling();
        }
      }
    };

    poll();
    intervalId = setInterval(poll, VENDOR_INVOICE_POLLING.PREVIEW_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [previewJobId]);

  const acceptMutation = useMutation({
    mutationFn: () => acceptVendorGuidancePreview(previewJobId),
    onSuccess: () => {
      toast.success(isCreate ? 'Vendor created.' : 'Vendor updated.');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
      });
      goToList({ cancelPreview: false });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save vendor.');
    },
  });

  // Apply a coach suggestion into the form WITHOUT saving. The Guidance
  // textarea is disabled while a preview job is ready, so the preview job
  // must be cleared before writing the form field. Both the "Apply
  // suggestion" button and an `apply:true` guidance event run this one path.
  const applyGuidance = useCallback(
    (text) => {
      const guidance = String(text ?? '');
      if (!guidance.trim()) {
        return;
      }
      const jobId = previewJobId;
      setPreviewJobId(null);
      setPreview(null);
      setTesting(false);
      setBlindMode(false);
      setLiveChatMessages(null);
      setChatLoading(false);
      setLatestSuggestedGuidance(null);
      setForm((f) => ({ ...f, guidance }));
      toast.success(
        'Applied suggested guidance. Run a test to validate it before saving.',
      );
      if (jobId) {
        // Fire-and-forget cleanup; wrap so .catch is always on a promise.
        Promise.resolve(
          cancelVendorGuidancePreview(jobId, { keepAttachments: true }),
        ).catch(() => {});
      }
    },
    [previewJobId],
  );

  const previewReady = preview?.status === JOB_STATUS.PENDING_REVIEW;
  const actionsBusy = acceptMutation.isPending || chatLoading;

  const rawChatMessages = liveChatMessages ?? preview?.messages ?? [];

  // Filter messages to show only conversational content
  const chatMessages = useMemo(() => {
    return rawChatMessages.filter((msg) => {
      // Hide tool calls and tool results
      if (msg.role === 'tool_call' || msg.role === 'tool') {
        return false;
      }

      // Keep user messages
      if (msg.role === 'user') {
        return true;
      }

      // For assistant/agent messages, only keep if it's actual conversational text
      if (msg.role === 'assistant' || msg.role === 'agent') {
        const text = msg.text || '';
        const trimmedText = text.trim();

        // Hide if empty
        if (!trimmedText) {
          return false;
        }

        // Hide if it looks like JSON data
        if (trimmedText.startsWith('{') || trimmedText.startsWith('[')) {
          return false;
        }

        return true;
      }

      // Hide system messages
      if (msg.role === 'system') {
        return false;
      }

      // Keep any other message types as a fallback
      return true;
    });
  }, [rawChatMessages]);

  const previewModalOpen = Boolean(testing || preview);

  const closePreviewModal = useCallback(async () => {
    if (acceptMutation.isPending) {
      return;
    }
    const jobId = previewJobId;
    setPreviewJobId(null);
    setPreview(null);
    setTesting(false);
    setBlindMode(false);
    setLatestSuggestedGuidance(null);
    setLiveChatMessages(null);
    setChatLoading(false);
    if (jobId) {
      try {
        await cancelVendorGuidancePreview(jobId, { keepAttachments: true });
      } catch {
        // best-effort cleanup
      }
    }
  }, [previewJobId, acceptMutation.isPending]);

  // One guidance-coach turn. A typed turn seeds the operator's message bubble;
  // a system-initiated blind start (blindStart:true) runs with no operator
  // message - the coach reads the uploaded invoice sample on its own and comes
  // back with a starting draft plus clarifying questions.
  const runCoachTurn = useCallback(
    async ({ jobId, feedback, blindStart = false }) => {
      if (!jobId) {
        return;
      }

      setChatLoading(true);

      const abortController = new AbortController();
      chatAbortRef.current = abortController;

      const base = [...(preview?.messages ?? [])];
      const seeded = blindStart
        ? base
        : [...base, { id: `user-${Date.now()}`, role: 'user', text: feedback }];
      setLiveChatMessages(seeded);

      let applyRequested = null;
      try {
        for await (const evt of streamVendorInvoiceGuidanceChat({
          jobId,
          feedback: blindStart ? undefined : feedback,
          blindStart,
          signal: abortController.signal,
        })) {
          if (evt.type === 'user_prompt' || evt.type === 'end') {
            continue;
          }

          if (evt.type === 'guidance' && typeof evt.guidance === 'string') {
            setLatestSuggestedGuidance(evt.guidance);
            if (evt.apply) {
              applyRequested = evt.guidance;
            }
            continue;
          }

          if (evt.type === 'error') {
            toast.error(evt.message || 'Guidance chat failed.');
            continue;
          }

          setLiveChatMessages((prev) =>
            applyReviewChatEvent(prev ?? seeded, evt),
          );
        }

        if (applyRequested !== null) {
          // Coach resolved an "apply those" intent — run the single apply path.
          applyGuidance(applyRequested);
          return;
        }

        const data = await fetchVendorGuidancePreview(jobId);
        setPreview(data);
        setLiveChatMessages(null);
      } catch (err) {
        if (
          chatAbortRef.current?.signal?.aborted ||
          err.name === 'AbortError'
        ) {
          // User cancelled - keep partial messages visible
        } else {
          toast.error(err.message || 'Failed to send feedback.');
          setLiveChatMessages(null);
        }
      } finally {
        chatAbortRef.current = null;
        setChatLoading(false);
      }
    },
    [preview, applyGuidance],
  );

  const handlePreviewChatSend = async (message) => {
    const trimmed = message.trim();
    if (!trimmed || !previewJobId || !previewReady || chatLoading) {
      return;
    }
    await runCoachTurn({ jobId: previewJobId, feedback: trimmed });
  };

  // Suggest-guidance (blind start) runs an opening coach turn automatically the
  // moment the session opens, so the operator never has to type first.
  useEffect(() => {
    if (!blindAutoStartJobId) {
      return;
    }
    const jobId = blindAutoStartJobId;
    setBlindAutoStartJobId(null);
    runCoachTurn({ jobId, blindStart: true });
  }, [blindAutoStartJobId, runCoachTurn]);

  const handlePreviewChatStop = () => chatAbortRef.current?.abort();

  if (!hydrated) {
    return (
      <div className="container" style={pageStyle}>
        <p style={emptyStyle}>Loading vendor…</p>
      </div>
    );
  }

  return (
    <div className="container scrollable" style={pageStyle}>
      <Link
        to={VENDOR_INVOICE_ROUTES.VENDORS}
        className="clickable-link"
        style={{
          display: 'inline-block',
          marginBottom: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        ← Back to vendors
      </Link>
      <p className="title">{isCreate ? 'New vendor' : form.name}</p>
      <p className="sub-title">
        Test guidance against a sample invoice, chat with the invoice-aware
        coach to refine it, then accept to save.
      </p>

      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          maxWidth: 960,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <h2 style={{ ...sectionTitleStyle, marginBottom: 0 }}>
            {isCreate ? 'Create vendor' : 'Edit vendor'}
          </h2>
          <button type="button" style={btnSecondary} onClick={() => goToList()}>
            Cancel
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem 1rem',
            maxWidth: 900,
            padding: '1rem',
            border: '1px solid var(--lighterGrey)',
            borderRadius: '0.35rem',
          }}
        >
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              fontSize: '0.85rem',
              color: 'var(--darkGrey)',
            }}
          >
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. SmithRX"
              required
              disabled={Boolean(previewJobId)}
              style={{
                ...fieldControlStyle,
                background: previewJobId ? 'var(--lightestGrey)' : undefined,
              }}
            />
          </label>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              fontSize: '0.85rem',
              color: 'var(--darkGrey)',
            }}
          >
            <span>Reason code (optional)</span>
            <Select
              value={
                reasonCodeOptions.find(
                  (opt) => opt.value === form.reasonCode,
                ) || null
              }
              onChange={(selected) =>
                setForm((f) => ({
                  ...f,
                  reasonCode: selected?.value || '',
                }))
              }
              options={reasonCodeOptions}
              placeholder="Search or select..."
              isClearable
              isSearchable
              isDisabled={Boolean(previewJobId) && previewReady}
              isLoading={reasonCodesLoading}
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  ...fieldControlStyle,
                  border:
                    validationAttempted && !form.reasonCode.trim()
                      ? '1px solid var(--red)'
                      : '1px solid var(--lighterGrey)',
                  background: state.isDisabled
                    ? 'var(--lightestGrey)'
                    : 'var(--white)',
                  minHeight: 'unset',
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '0 0.75rem',
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
              }}
            />
          </label>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              fontSize: '0.85rem',
              color: 'var(--darkGrey)',
            }}
          >
            <span>Supplier HCC ID</span>
            <input
              type="text"
              value={form.supplierHccID}
              onChange={(e) =>
                setForm((f) => ({ ...f, supplierHccID: e.target.value }))
              }
              placeholder="FINONLY…"
              disabled={Boolean(previewJobId) && previewReady}
              style={{
                ...fieldControlStyle,
                background:
                  Boolean(previewJobId) && previewReady
                    ? 'var(--lightestGrey)'
                    : undefined,
              }}
            />
          </label>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              gridColumn: '1 / -1',
            }}
          >
            <GuidanceSectionsHint
              onInsertTemplate={() =>
                setForm((f) => ({
                  ...f,
                  guidance: GUIDANCE_TEMPLATE,
                }))
              }
            />
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
                fontSize: '0.85rem',
                color: 'var(--darkGrey)',
              }}
            >
              <span>Guidance</span>
              <textarea
                value={form.guidance}
                onChange={(e) =>
                  setForm((f) => ({ ...f, guidance: e.target.value }))
                }
                rows={16}
                required
                disabled={Boolean(previewJobId) && previewReady}
                style={{
                  ...fieldControlStyle,
                  resize: 'vertical',
                  minHeight: '14rem',
                  lineHeight: 1.4,
                  background:
                    Boolean(previewJobId) && previewReady
                      ? 'var(--lightestGrey)'
                      : undefined,
                }}
              />
            </label>
          </div>
        </div>

        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        >
          <h3 style={sectionTitleStyle}>Test with an invoice</h3>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem',
              maxWidth: 900,
              fontSize: '0.85rem',
              color: 'var(--darkGrey)',
            }}
          >
            <span>Expected payables (optional)</span>
            <textarea
              value={expectedPayables}
              onChange={(e) => setExpectedPayables(e.target.value)}
              rows={5}
              placeholder={
                'One per line: the account, then the amount, e.g.\nACME 120.00'
              }
              aria-describedby="expected-payables-hint"
              disabled={Boolean(previewJobId) && previewReady}
              style={{
                ...fieldControlStyle,
                resize: 'vertical',
                lineHeight: 1.4,
                background:
                  Boolean(previewJobId) && previewReady
                    ? 'var(--lightestGrey)'
                    : undefined,
              }}
            />
          </label>
          <p id="expected-payables-hint" style={{ ...noteStyle, margin: 0 }}>
            The test then shows which payables match. An account matches a
            payable whose account starts with it, so ACME matches &quot;ACME -
            1001&quot;.
          </p>
          <VendorInvoiceUploadPanel
            pendingFiles={pendingFiles}
            addFiles={addFiles}
            removeFile={removeFile}
            primaryLabel="Test guidance"
            primaryLoadingLabel="Testing..."
            primaryBusy={testing}
            primaryDisabled={!canTest}
            onPrimary={handleTest}
            selectDisabled={Boolean(previewJobId) && previewReady}
            hint="Upload a sample invoice, run the graph with this guidance, then chat with the invoice-aware coach to refine it."
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {!form.guidance.trim() && (
              <button
                type="button"
                style={disabledBtn(btnSecondary, !canBlindStart)}
                disabled={!canBlindStart}
                onClick={handleBlindStart}
              >
                Draft starting guidance
              </button>
            )}
            <button
              type="button"
              style={disabledBtn(btnSecondary, !canSaveInvoice)}
              disabled={!canSaveInvoice}
              onClick={() => saveInvoiceMutation.mutate()}
            >
              {saveInvoiceMutation.isPending ? 'Saving…' : 'Save anyway'}
            </button>
          </div>
          {!form.guidance.trim() && (
            <p style={noteStyle}>
              No guidance yet? Upload a sample invoice and use{' '}
              <strong>Draft starting guidance</strong> to have the coach propose
              a first draft from the invoice.
            </p>
          )}
        </div>
      </section>

      <Modal
        isOpen={previewModalOpen}
        onClose={closePreviewModal}
        size="viewport"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            width: '100%',
            height: '100%',
            minHeight: 0,
            boxSizing: 'border-box',
            paddingRight: '1.5rem',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--black)',
            }}
          >
            {blindMode ? 'Draft starting guidance' : 'Guidance test'}
          </h2>

          {testing &&
            !previewReady &&
            preview?.status !== JOB_STATUS.FAILED && (
              <p style={{ ...noteStyle, marginBottom: 0 }}>
                Processing invoice with your draft guidance…
                {preview?.status ? ` (${preview.status})` : ''}
              </p>
            )}

          {preview?.status === JOB_STATUS.FAILED && (
            <p style={{ color: 'var(--red)', margin: 0 }}>
              {preview.failureReason || 'Test failed.'}
            </p>
          )}

          {previewReady && (
            <VendorInvoiceReviewSplit
              autoSaveId="vendor-guidance-preview"
              style={{ flex: 1, minHeight: 0 }}
              left={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    paddingBottom: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                    }}
                  >
                    {!blindMode && (
                      <button
                        type="button"
                        style={disabledBtn(btnPrimary, actionsBusy)}
                        disabled={actionsBusy}
                        onClick={() => acceptMutation.mutate()}
                      >
                        {acceptMutation.isPending
                          ? 'Saving…'
                          : 'Accept & save vendor'}
                      </button>
                    )}
                    <button
                      type="button"
                      style={disabledBtn(
                        btnSecondary,
                        actionsBusy || !latestSuggestedGuidance,
                      )}
                      disabled={actionsBusy || !latestSuggestedGuidance}
                      onClick={() => applyGuidance(latestSuggestedGuidance)}
                    >
                      Apply suggestion
                    </button>
                    <button
                      type="button"
                      style={disabledBtn(btnSecondary, actionsBusy)}
                      disabled={actionsBusy}
                      onClick={closePreviewModal}
                    >
                      Discard test
                    </button>
                  </div>
                  {latestSuggestedGuidance && (
                    <p style={{ ...noteStyle, margin: 0 }}>
                      A suggested guidance is ready. Apply it to load it into
                      the form, then run a test to validate it.
                    </p>
                  )}
                  {!blindMode && preview.expectedCheck && (
                    <ExpectedPayablesCheck check={preview.expectedCheck} />
                  )}
                  {blindMode ? (
                    <p style={noteStyle}>
                      No test has been run yet. Chat with the coach to draft a
                      starting guidance from the uploaded invoice, then apply
                      it.
                    </p>
                  ) : (
                    <VendorInvoicePayablesTable
                      payables={preview.payables ?? []}
                      isPreviewMode={true}
                      readOnly
                    />
                  )}
                </div>
              }
              chatProps={{
                disabled: actionsBusy,
                loading: chatLoading,
                messages: chatMessages,
                onSend: handlePreviewChatSend,
                onStop: handlePreviewChatStop,
                title: 'Guidance coach',
                subtitle:
                  'Ask the invoice-aware coach how to write your guidance, then apply its suggestion.',
                emptyHint: blindMode
                  ? 'Ask the coach to draft a starting guidance from this invoice — e.g. "Draft guidance for this invoice."'
                  : 'Ask how to change your guidance to fix the payables — e.g. "Why are there too many payables?" Say "apply those" to load a suggestion into the form.',
                placeholder: 'Ask the guidance coach…',
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VendorConfigurationEditor;
